import type { Configuration } from '@repo/api-gw-sdk';
import { GdsgEnvironmentsApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/gdsg';

interface GdsgRegionStatsPointResponse {
  bucketStartMs: number;
  membersProcessed: number;
  failures: number;
  queueRowsProcessed: number;
  promoteLatencyP95Ms: number;
  replicationLagP95Ms: number;
  putAckToBackupLagP95Ms: number;
  bundleSizeAvgBytes: number;
  sampleError?: string;
}

export interface GdsgRegionStatsResponse {
  region: string;
  enabled: boolean;
  points?: GdsgRegionStatsPointResponse[];
  totalStorageBytes?: number;
  storageObservedAtMs?: number;
  membersProcessedTotal?: number;
  failuresTotal?: number;
  replicationLagP95MsMax?: number;
  putAckToBackupLagP95MsMax?: number;
  sampleError?: string;
}

interface GdsgStorageByTierEntryResponse {
  storageClass?: string;
  totalStorageBytes?: number;
}

export interface GetGdsgRegionStatsResponse {
  windowStartMs: number;
  windowEndMs: number;
  bucketIntervalMs: number;
  regions?: GdsgRegionStatsResponse[];
  storageByTier?: GdsgStorageByTierEntryResponse[];
}

export interface GdsgActiveWorkersResponse {
  consolidation?: number;
  replication?: number;
}

export interface GdsgRegisteredWorkerResponse {
  stage?: string;
  workerId?: string;
  lastSeenMs?: number;
  draining?: boolean;
  drainRequested?: boolean;
}

export interface GdsgBigtableClusterStatsResponse {
  serveNodes?: number;
  cpuLoad?: number;
  diskBytesUsed?: number;
  observedAtMs?: number;
}

export interface GdsgDevStatsResponse {
  region: string;
  enabled: boolean;
  activeWorkers?: GdsgActiveWorkersResponse;
  workerRegistry?: GdsgRegisteredWorkerResponse[];
  bigtable?: GdsgBigtableClusterStatsResponse;
}

export interface GetGdsgDevStatsResponse {
  observedAtMs: number;
  regions?: GdsgDevStatsResponse[];
}

export const gdsg = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const gdsgApi = new GdsgEnvironmentsApi(config);

  return {
    useListEnvironments: () =>
      httpClient.execute(
        gdsgApi.listGdsgEnvironments.bind(gdsgApi),
        baseUrl,
        'listGdsgEnvironments',
        currentProjectId
      ),
    // useGetRegionStats hits the api-gateway region-stats endpoint directly
    // (instead of going through the generated SDK class) so the FE can ship
    // ahead of the next OpenAPI codegen pass. Swap to the generated method
    // once `getGdsgRegionStats` lands in @repo/api-gw-sdk.
    useGetRegionStats: (
      environmentId: string | undefined,
      sinceMs: number,
      untilMs: number,
      bucketIntervalMs?: number
    ) => {
      const query = new URLSearchParams({
        sinceMs: String(sinceMs),
        untilMs: String(untilMs),
      });
      if (bucketIntervalMs) {
        query.set('bucketIntervalMs', String(bucketIntervalMs));
      }
      const url = environmentId
        ? `/projects/${currentProjectId}/gdsg/environments/${environmentId}/region-stats?${query.toString()}`
        : '';
      return httpClient.get(url, {
        refreshInterval: 30_000,
        // Empty `regions` array is the "no data yet" signal the metrics
        // section uses to render its empty state. We don't retry on error
        // so a transient failure shows the empty state once instead of
        // looping through SWR's backoff retries.
        shouldRetryOnError: false,
      }) as {
        body: GetGdsgRegionStatsResponse | undefined;
        isLoading: boolean;
        error: unknown;
      };
    },
    // useGetDevStats fetches operator-grade diagnostics (worker counts,
    // worker-registry detail, Bigtable cluster shape) for the dev-mode
    // panel. The endpoint is normal-auth on the api-gateway; passing
    // enabled=false makes the URL empty so SWR never fires the request.
    // Caller gates `enabled` on the dev-mode predicate (isDev OR
    // isEmployee && !isDemo && pref.isDevMode) — see gdsgMetricsSection.
    useGetDevStats: (environmentId: string | undefined, enabled: boolean) => {
      const url =
        enabled && environmentId
          ? `/projects/${currentProjectId}/gdsg/environments/${environmentId}/dev-stats`
          : '';
      return httpClient.get(url, {
        refreshInterval: 10_000,
        shouldRetryOnError: false,
      }) as {
        body: GetGdsgDevStatsResponse | undefined;
        isLoading: boolean;
        error: unknown;
      };
    },
  };
};
